import React, { useState, useContext, useEffect } from 'react';
import {
  Link2, Users,
  Zap, TrendingUp,
  Linkedin, MessageSquare, ChevronRight,
  Instagram
} from 'lucide-react';
import { ThemeContext } from '../utils/context';
import { useAuth } from '../utils/AuthProvider';
import { rootApi, senvraApi } from '../utils/axiosInstance';

const ReferEarn = () => {
  const { isDarkMode } = useContext(ThemeContext) || {};
  const { user } = useAuth();

  const [referralData, setReferralData] = useState({ code: '', link: '' });
  const [stats, setStats] = useState({
    successful: 0,
    pending: 0,
    referredUsers: []
  });

  const [copyStatus, setCopyStatus] = useState(false);
  const [linkCopyStatus, setLinkCopyStatus] = useState(false);
  const [emailInvite, setEmailInvite] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [showAllLogs, setShowAllLogs] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Direct JWT decode as a fail-safe
      const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
      let decodedEmail = null;
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
          decodedEmail = JSON.parse(jsonPayload).email;
        } catch (e) {
          console.error("JWT Decode error:", e);
        }
      }

      const activeEmail = user?.email || decodedEmail || JSON.parse(localStorage.getItem('current_user') || '{}').email;

      if (!activeEmail) {
        console.warn("No user email found in Context, JWT, or Storage. Please log in.");
        return;
      }

      try {
        console.log(`Hitting endpoints for: ${activeEmail}`);

        const authToken = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
        const config = {
          timeout: 5000,
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        };

        // Fetching from the new unified stats endpoint
        const statsRes = await senvraApi.get(`/referral/stats`, config);

        // ✅ USE API'S referral_link DIRECTLY — no manual construction
        const refCode = statsRes.data.referral_code;
        const generatedLink = statsRes.data.referral_link || `https://www.senvraai.com/register?ref=${refCode}`;

        setReferralData({
          code: refCode,
          link: generatedLink
        });

        setStats({
          successful: statsRes.data.total_referrals,
          pending: 0,
          referredUsers: statsRes.data.referred_users || []
        });
      } catch (error) {
        console.error("Error fetching referral data:", error);
      } finally {
        // no cleanup needed
      }
    };

    fetchData();
  }, [user?.email]);

  const theme = {
    bg: isDarkMode ? '#0F172A' : '#F8FAFC',
    card: isDarkMode ? '#1E293B' : '#FFFFFF',
    cardBorder: isDarkMode ? '#334155' : '#F1F5F9',
    textPrimary: isDarkMode ? '#F8FAFC' : '#0F172A',
    textSecondary: isDarkMode ? '#94A3B8' : '#475569',
    accent: '#0284C7',
    success: '#10B981',
    pending: '#F59E0B'
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralData.code);
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralData.link);
    setLinkCopyStatus(true);
    setTimeout(() => setLinkCopyStatus(false), 2000);
  };

  const handleInstagramShare = () => {
    navigator.clipboard.writeText(`Check out these courses on Senvra AI! Use my code ${referralData.code}: ${referralData.link}`);
    setLinkCopyStatus(true);
    setTimeout(() => setLinkCopyStatus(false), 2000);
    window.open(`https://www.instagram.com/`, '_blank');
  };

  const shareOnWhatsApp = () => {
    const textShare = encodeURIComponent(`Join me at Senvra AI! Use my referral code ${referralData.code} to apply here: ${referralData.link}`);
    window.open(`https://wa.me/?text=${textShare}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralData.link)}`, '_blank');
  };

  const handleEmailInvite = async () => {
    if (!emailInvite || !emailInvite.includes('@')) {
      alert("Please enter a valid email address");
      return;
    }

    // Robust email detection (matching useEffect logic)
    const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
    let decodedEmail = null;
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        decodedEmail = JSON.parse(jsonPayload).email;
      } catch (e) {
        console.error("JWT Decode error:", e);
      }
    }

    const activeEmail = user?.email || decodedEmail || JSON.parse(localStorage.getItem('current_user') || '{}').email;

    if (!activeEmail) {
      alert("Please log in to send invites");
      return;
    }

    try {
      setInviteLoading(true);
      setInviteResult(null);
      const authToken = localStorage.getItem('accessToken') || localStorage.getItem('access_token');

      const response = await rootApi.post('/referral/invite', {
        to_email: emailInvite
      }, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });

      if (response.data.success) {
        setInviteResult({
          type: 'success',
          message: response.data.message || "Referral link shared successfully!",
          link: referralData.link
        });
        setEmailInvite('');
        setTimeout(() => setInviteResult(null), 10000);
      } else {
        throw new Error(response.data.message || "Failed to send invite");
      }
    } catch (error) {
      console.error("Error sending invite:", error);
      setInviteResult({
        type: 'error',
        message: error.response?.data?.message || error.message || "Failed to send referral invite."
      });
    } finally {
      setInviteLoading(false);
    }
  };

  // ✅ QR CODE URL — dynamically generated from API's referral_link
  const qrCodeUrl = referralData.link
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(referralData.link)}`
    : '';

  return (
    <main className="flex-grow overflow-x-hidden transition-colors duration-300" style={{ backgroundColor: theme.bg }}>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Outfit:wght@100..900&display=swap');
          .font-playfair { font-family: 'Playfair Display', serif; }
          .font-outfit { font-family: 'Outfit', sans-serif; }
          .animate-shimmer {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
            background-size: 200% 100%;
            animation: shimmer 2s infinite;
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>

      {/* HERO SECTION */}
      <section className="pt-[140px] md:pt-[180px] pb-[60px] md:pb-[80px] px-5 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-100/30 rounded-full blur-[120px] -z-10"></div>
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-purple-100/20 rounded-full blur-[120px] -z-10"></div>

        <h1 className="text-[clamp(2.5rem,6vw,5.5rem)] leading-[1.1] mb-6 font-medium font-playfair" style={{ color: theme.textPrimary }}>
          Learn & Earn Together.
        </h1>

        <p className="text-base md:text-[19px] mb-[45px] max-w-2xl mx-auto font-medium leading-relaxed" style={{ color: theme.textSecondary }}>
          Invite your friends to explore our world-class courses and get rewarded when they start their learning journey with Senvra.
        </p>
      </section>

      {/* DASHBOARD GRID */}
      <section className="pb-32 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

            {/* LEFT COLUMN: Stats & Progress — sticky so it doesn't leave a void */}
            <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-[100px] lg:self-start">

              {/* Earnings Metrics */}
              <div className="bg-white dark:bg-slate-800 rounded-[32px] p-8 border border-slate-100 dark:border-slate-700 shadow-[0_8px_30px_rgba(0,0,0,0.06)] relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/70 dark:bg-emerald-900/10 rounded-bl-[100px] -z-0"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-10">
                    <h3 className="text-[11px] font-black uppercase tracking-[2px] text-slate-400">Your Impact</h3>
                    <TrendingUp size={20} className="text-[#0284C7]" />
                  </div>

                  <div className="space-y-8">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest mb-1 text-slate-400">Successful Invites</p>
                        <p className="text-2xl font-bold" style={{ color: theme.textPrimary }}>{stats.successful}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest mb-1 text-slate-400">Pending</p>
                        <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Records Grid */}
              <div className="bg-white dark:bg-slate-800 rounded-[32px] p-8 border border-slate-100 dark:border-slate-700 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-[11px] font-black uppercase tracking-[2px] text-slate-400">Referrals Board</h3>
                  <Users size={18} className="text-slate-300" />
                </div>
                <div className="space-y-6">
                  {stats.referredUsers.length > 0 ? (
                    (showAllLogs ? stats.referredUsers : stats.referredUsers.slice(0, 3)).map((ref, i) => {
                      // Map real backend fields with safe fallbacks
                      const displayName = ref.name || ref.masked_email || 'Anonymous';
                      const initials = displayName.slice(0, 2).toUpperCase();
                      const joinedDate = ref.joined_on || ref.referred_at || ref.signup_date;
                      const dateLabel = joinedDate
                        ? new Date(joinedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'Recently joined';
                      const status = ref.status || 'Success';
                      const isSuccess = true; // New API seems to only return successful ones or we assume success for the board

                      return (
                        <div key={i} className="rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-blue-100 dark:hover:border-blue-900 group/row">
                          {/* Compact Row */}
                          <div className="flex items-center gap-4 px-5 py-4 bg-white dark:bg-slate-800 transition-colors group-hover/row:bg-slate-50/50 dark:group-hover/row:bg-slate-900/50">
                            {/* Avatar */}
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-sky-400 flex items-center justify-center text-white text-[11px] font-black flex-shrink-0 shadow-md group-hover/row:scale-110 transition-transform duration-300">
                              {initials}
                            </div>
                            {/* Name + date */}
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-bold truncate group-hover/row:text-blue-600 transition-colors" style={{ color: theme.textPrimary }}>{displayName}</p>
                              <p className="text-[10px] font-medium text-slate-400 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-slate-300" /> {dateLabel}
                              </p>
                            </div>
                            {/* Status badge */}
                            <div className="flex flex-col items-end gap-1">
                              <span className={`text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-wider border shadow-sm ${isSuccess ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                                }`}>{status}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    /* Rich empty state */
                    <div className="py-6 flex flex-col items-center text-center gap-5">
                      <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
                        <Users size={28} />
                      </div>
                      <div>
                        <p className="text-base font-bold font-playfair" style={{ color: theme.textPrimary }}>No referrals yet</p>
                        <p className="text-[12px] text-slate-400 mt-1 max-w-[240px] leading-relaxed">Share your link or code and you'll see everyone who joins here in real time.</p>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(referralData.link)}
                        className="w-full py-3.5 rounded-2xl bg-[#0F172A] text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all"
                      >
                        Share My Referral Link →
                      </button>
                    </div>
                  )}
                </div>
                {stats.referredUsers.length > 3 && (
                  <button
                    onClick={() => setShowAllLogs(!showAllLogs)}
                    className="w-full mt-8 py-4 text-[10px] font-black uppercase tracking-[2px] text-slate-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                  >
                    {showAllLogs ? "Show Less" : "View Full Tracking Details"} <ChevronRight size={14} className={showAllLogs ? "rotate-90" : ""} />
                  </button>
                )}
              </div>

            </div>

            {/* RIGHT COLUMN: Console & Records */}
            <div className="lg:col-span-8 space-y-8">

              {/* Console Card */}
              <div className="bg-white dark:bg-slate-800 rounded-[32px] p-8 md:p-12 border border-slate-100 dark:border-slate-700 shadow-[0_8px_30px_rgba(0,0,0,0.06)] relative overflow-hidden group h-fit">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 dark:bg-blue-900/5 rounded-bl-[200px] -z-0"></div>

                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-12">
                    <div className="w-14 h-14 rounded-2xl bg-[#0F172A] flex items-center justify-center text-white shadow-xl shadow-slate-900/20">
                      <Zap size={24} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold font-playfair text-slate-900 dark:text-white">Share with Friends</h3>
                      <p className="text-[11px] font-bold uppercase tracking-[2px] text-slate-400">Invite friends to join our courses</p>
                    </div>
                  </div>

                  <div className="animate-in zoom-in-95 duration-500 space-y-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      {/* Share Tools */}
                      <div className="space-y-8">
                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-400">Quick Share</p>
                          <div className="flex gap-4">
                            <button onClick={shareOnWhatsApp} className="flex-1 py-4 rounded-2xl bg-emerald-50 text-emerald-600 flex flex-col items-center gap-2 hover:bg-emerald-100 transition-all border border-emerald-100/50">
                              <MessageSquare size={20} />
                              <span className="text-[10px] font-black uppercase">WhatsApp</span>
                            </button>
                            <button onClick={shareOnLinkedIn} className="flex-1 py-4 rounded-2xl bg-blue-50 text-blue-600 flex flex-col items-center gap-2 hover:bg-blue-100 transition-all border border-blue-100/50">
                              <Linkedin size={20} />
                              <span className="text-[10px] font-black uppercase">LinkedIn</span>
                            </button>
                            <button onClick={handleInstagramShare} className="flex-1 py-4 rounded-2xl bg-rose-50 text-rose-600 flex flex-col items-center gap-2 hover:bg-rose-100 transition-all border border-rose-100/50">
                              <Instagram size={20} />
                              <span className="text-[10px] font-black uppercase">{linkCopyStatus ? 'Copied!' : 'Instagram'}</span>
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-400">Invite via Email</p>
                          <div className="relative">
                            <input
                              type="email"
                              placeholder="friend@email.com"
                              value={emailInvite}
                              onChange={(e) => setEmailInvite(e.target.value)}
                              className="w-full rounded-2xl py-4 px-6 outline-none border-2 border-slate-100 dark:border-slate-700 focus:border-blue-500 transition-all bg-slate-50/50 dark:bg-slate-900/50"
                              style={{ color: theme.textPrimary }}
                            />
                            <button
                              onClick={handleEmailInvite}
                              disabled={inviteLoading}
                              className={`absolute right-2 top-2 bottom-2 px-6 rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all ${inviteLoading ? 'bg-slate-400' : 'bg-[#0F172A] hover:opacity-90'}`}
                            >
                              {inviteLoading ? 'Sending...' : 'Send'}
                            </button>
                          </div>
                          {inviteResult && (
                            <div className={`mt-3 p-3 rounded-xl text-[11px] font-medium border animate-in fade-in slide-in-from-top-2 ${
                              inviteResult.type === 'success'
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-600'
                                : 'bg-rose-50 border-rose-100 text-rose-600'
                            }`}>
                              {inviteResult.message}
                              {inviteResult.link && (
                                <div className="mt-1 flex items-center gap-2">
                                  <span className="opacity-70">Link:</span>
                                  <code className="bg-white/50 px-1.5 py-0.5 rounded border border-emerald-200/50 truncate max-w-[200px]">{inviteResult.link}</code>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* QR Box */}
                      <div className="p-8 rounded-[32px] border-2 border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col items-center justify-center gap-4 text-center group/qr hover:border-blue-500/30 transition-all duration-300">
                        <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 group-hover/qr:text-blue-500 transition-colors">Scan to Share</p>
                        <a
                          href={referralData.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative block group-hover/qr:scale-105 transition-transform duration-500"
                        >
                          {/* ✅ DYNAMIC QR FROM API referra_link */}
                          <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32 rounded-2xl shadow-xl bg-white p-2 border border-slate-100" />
                          <div className="absolute inset-0 bg-blue-600/5 rounded-2xl opacity-0 group-hover/qr:opacity-100 transition-opacity flex items-center justify-center">
                            <Link2 size={24} className="text-blue-600 opacity-0 group-hover/qr:opacity-100 translate-y-2 group-hover/qr:translate-y-0 transition-all duration-300" />
                          </div>
                        </a>
                        <p className="text-[10px] font-medium text-slate-500 max-w-[120px]">Click or scan to open your link instantly</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-8 rounded-[32px] bg-[#F0F8FF] border border-[#E0F2FE] flex flex-col gap-4">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[2px] text-[#0284C7] mb-1">Your Referral Code</p>
                          <p className="text-3xl font-black tracking-[4px] text-slate-900 uppercase font-playfair">{referralData.code || "---"}</p>
                          <p className="text-[10px] font-medium text-blue-400 mt-2">Friends can type this manually when they sign up</p>
                        </div>
                        <button onClick={handleCopyCode} className="px-6 py-3 rounded-xl border-2 border-blue-200 text-[#0284C7] font-bold text-xs uppercase tracking-widest hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all">
                          {copyStatus ? 'Copied!' : 'Copy Code'}
                        </button>
                      </div>

                      <div className="p-8 rounded-[32px] bg-slate-50 border border-slate-200 flex flex-col gap-4">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 mb-1">Your Referral Link</p>
                          <p className="text-sm font-bold text-slate-600 truncate">{referralData.link || "---"}</p>
                          <p className="text-[10px] font-medium text-slate-400 mt-2">Your code is automatically applied via this link</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={handleCopyLink} className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all">
                            {linkCopyStatus ? 'Copied!' : 'Copy Link'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div> {/* End of lg:col-span-8 */}
          </div> {/* End of grid-cols-12 */}

        </div>
      </section>
    </main>
  );
};

export default ReferEarn;